import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Eye, EyeOff, FilePlus2, FileText, Loader2, RotateCcw, Save, Trash2,
} from 'lucide-react';
import client from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { renderSettingText, TEXT_PLACEHOLDERS } from '../lib/textTemplate';
import PageContent from '../components/PageContent';
import { useToast } from '../hooks/use-toast';
import { AdminError, AdminSkeleton } from './components/PageState';

const emptyPage = {
  title: 'Yeni Sayfa',
  slug: 'yeni-sayfa',
  content: '',
  is_active: true,
  order: 0,
};

const slugify = (value) => value
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 160);

const FooterPages = () => {
  const settings = useSettings();
  const { toast } = useToast();
  const [pages, setPages] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const contentRef = useRef(null);

  const selected = useMemo(
    () => pages?.find((page) => page.id === selectedId),
    [pages, selectedId],
  );

  const load = async () => {
    setError('');
    try {
      const { data } = await client.get('/admin/footer-pages');
      const items = data.items || [];
      setPages(items);
      setSelectedId((current) => (
        items.some((page) => page.id === current) ? current : items[0]?.id || null
      ));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Footer sayfaları yüklenemedi');
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selected) setDraft({ ...selected });
    else if (selectedId === 'new') setDraft({ ...emptyPage });
    else setDraft(null);
  }, [selected, selectedId]);

  const chooseNew = () => {
    const usedSlugs = new Set((pages || []).map((page) => page.slug));
    let slug = emptyPage.slug;
    let suffix = 2;
    while (usedSlugs.has(slug)) {
      slug = `${emptyPage.slug}-${suffix}`;
      suffix += 1;
    }
    setSelectedId('new');
    setDraft({ ...emptyPage, slug, order: (pages?.length || 0) + 1 });
  };

  const save = async () => {
    if (!draft?.title.trim() || !draft?.slug.trim()) {
      toast({ title: 'Başlık ve sayfa adresi zorunlu' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        slug: slugify(draft.slug),
        content: draft.content || '',
        is_active: !!draft.is_active,
        order: Number(draft.order) || 0,
      };
      const { data } = selectedId === 'new'
        ? await client.post('/admin/footer-pages', payload)
        : await client.patch(`/admin/footer-pages/${selectedId}`, payload);
      setPages((current) => {
        const withoutSaved = current.filter((page) => page.id !== data.id);
        return [...withoutSaved, data].sort(
          (first, second) => (first.order || 0) - (second.order || 0),
        );
      });
      setSelectedId(data.id);
      setDraft({ ...data });
      toast({ title: 'Footer sayfası kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Sayfa kaydedilemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setSaving(false);
    }
  };

  // Varsayılan metinler yalnızca kurulumun ilk açılışında yazılır; daha önce
  // kurulmuş sitede eski (kısa) metinler kalır. Panelden yeniden yüklemenin
  // iki kipi vardır: eksikleri tamamlamak güvenlidir, hepsini yeniden yazmak
  // yöneticinin kendi metnini siler — bu yüzden ikincisi ayrıca onay ister.
  const restoreDefaults = async (overwrite) => {
    if (overwrite && !window.confirm(
      'Varsayılan adreslere sahip sayfaların başlık ve içerikleri '
      + 'varsayılan metinlerle DEĞİŞTİRİLECEK. Kendi yazdığınız metinler '
      + 'kaybolur. Devam edilsin mi?',
    )) return;
    setRestoring(true);
    try {
      const { data } = await client.post(
        `/admin/footer-pages/defaults?overwrite=${overwrite ? 'true' : 'false'}`,
      );
      await load();
      setDefaultsOpen(false);
      toast({
        title: 'Varsayılan içerikler yüklendi',
        description: `${data.created} sayfa eklendi, ${data.updated} sayfa güncellendi.`,
      });
    } catch (requestError) {
      toast({
        title: 'Varsayılan içerikler yüklenemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setRestoring(false);
    }
  };

  // Yer tutucu imlecin bulunduğu yere girer; yönetici metni bozmadan ekler.
  const insertPlaceholder = (key) => {
    const token = `{{${key}}}`;
    const field = contentRef.current;
    const value = draft?.content || '';
    if (!field) {
      setDraft((current) => ({ ...current, content: `${value}${token}` }));
      return;
    }
    const start = field.selectionStart ?? value.length;
    const end = field.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    setDraft((current) => ({ ...current, content: next }));
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const remove = async () => {
    if (!draft?.id || !window.confirm(`"${draft.title}" sayfası silinsin mi?`)) return;
    setSaving(true);
    try {
      await client.delete(`/admin/footer-pages/${draft.id}`);
      const remaining = pages.filter((page) => page.id !== draft.id);
      setPages(remaining);
      setSelectedId(remaining[0]?.id || null);
      toast({ title: 'Footer sayfası silindi' });
    } catch (requestError) {
      toast({
        title: 'Sayfa silinemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setSaving(false);
    }
  };

  if (error && !pages) return <AdminError message={error} onRetry={load} />;
  if (!pages) return <AdminSkeleton rows={8} label="Footer sayfaları yükleniyor" />;

  return (
    <div className="pb-20">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(56,189,248,0.15)', color: 'var(--v2-info, #38bdf8)' }}
        >
          <FileText size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Footer Sayfaları</h1>
          <p className="text-xs text-slate-400">Footer menülerinin açtığı sayfa içeriklerini yönetin</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDefaultsOpen((open) => !open)}
            className="admin-btn-secondary flex items-center gap-2"
          >
            <RotateCcw size={14} /> Varsayılan İçerikler
          </button>
          <button
            type="button"
            onClick={chooseNew}
            className="admin-btn-secondary flex items-center gap-2"
          >
            <FilePlus2 size={14} /> Yeni Sayfa
          </button>
        </div>
      </div>

      {defaultsOpen && (
        <div className="admin-card p-4 mb-5 border border-cyan-400/20">
          <h2 className="font-bold text-sm mb-1">Varsayılan sayfa içerikleri</h2>
          <p className="text-xs text-slate-400 mb-4">
            Kurulumla birlikte gelen hazır metinler; iletişim bilgileri, kurallar,
            rehberler ve sözlük dâhil. Tümü yer tutucularla yazılmıştır:
            alan adı ve iletişim bilgileri site ayarlarından otomatik gelir.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={restoring}
              onClick={() => restoreDefaults(false)}
              className="admin-btn-primary flex items-center gap-2"
            >
              {restoring ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} />}
              Eksik sayfaları ekle
            </button>
            <button
              type="button"
              disabled={restoring}
              onClick={() => restoreDefaults(true)}
              className="admin-btn-secondary flex items-center gap-2"
            >
              <RotateCcw size={14} /> Tümünü varsayılana döndür
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            <strong className="text-slate-400">Eksik sayfaları ekle</strong> yalnızca
            bulunmayan adresleri oluşturur, mevcut metinlerinize dokunmaz.
            <strong className="text-slate-400"> Tümünü varsayılana döndür</strong> ise
            varsayılan adreslerin başlık ve içeriğini yeniden yazar; yayın durumu ve
            sıra korunur.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-5">
        <aside className="admin-card p-3 h-fit">
          <div className="px-2 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            Sayfalar ({pages.length})
          </div>
          <div className="space-y-1 max-h-[680px] overflow-y-auto">
            {pages.map((page) => (
              <button
                type="button"
                key={page.id}
                onClick={() => setSelectedId(page.id)}
                className={`w-full rounded-lg px-3 py-3 text-left border transition-colors ${
                  selectedId === page.id
                    ? 'border-cyan-400/30 bg-cyan-400/10'
                    : 'border-transparent hover:bg-white/[0.04]'
                }`}
              >
                <span className="flex items-center gap-2">
                  {page.is_active
                    ? <Eye size={13} className="text-emerald-400" />
                    : <EyeOff size={13} className="text-slate-600" />}
                  <strong className="text-sm truncate">{page.title}</strong>
                </span>
                <span className="block mt-1 pl-5 text-[11px] text-slate-500 truncate">
                  /sayfa/{page.slug}
                </span>
              </button>
            ))}
            {!pages.length && (
              <div className="px-3 py-8 text-center text-sm text-slate-500">
                Henüz footer sayfası yok.
              </div>
            )}
          </div>
        </aside>

        <section className="admin-card p-5 md:p-6 min-h-[560px]">
          {draft ? (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <div>
                  <h2 className="font-bold">{selectedId === 'new' ? 'Yeni Sayfa' : draft.title}</h2>
                  <p className="text-xs text-slate-500">
                    Başlık ve içerikte yer tutucu kullanabilirsiniz; alan adı
                    tarayıcıdan, iletişim bilgileri site ayarlarından gelir.
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {draft.id && (
                    <a
                      href={`/sayfa/${draft.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="admin-btn-secondary flex items-center gap-2"
                    >
                      <Eye size={14} /> Görüntüle
                    </a>
                  )}
                  {draft.id && (
                    <button
                      type="button"
                      onClick={remove}
                      disabled={saving}
                      className="icon-btn ban"
                      title="Sayfayı sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="admin-btn-primary flex items-center gap-2"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Kaydet
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(240px,0.75fr)_100px] gap-3 mb-4">
                <Field label="Sayfa Başlığı">
                  <input
                    className="admin-input"
                    value={draft.title}
                    onChange={(event) => {
                      const title = event.target.value;
                      setDraft((current) => ({
                        ...current,
                        title,
                        slug: selectedId === 'new' ? slugify(title) : current.slug,
                      }));
                    }}
                  />
                </Field>
                <Field label="Sayfa Adresi">
                  <div className="flex items-center admin-input overflow-hidden">
                    <span className="text-slate-600 text-xs">/sayfa/</span>
                    <input
                      className="bg-transparent min-w-0 flex-1 outline-none"
                      value={draft.slug}
                      onChange={(event) => setDraft({
                        ...draft,
                        slug: slugify(event.target.value),
                      })}
                    />
                  </div>
                </Field>
                <Field label="Sıra">
                  <input
                    type="number"
                    min="0"
                    className="admin-input"
                    value={draft.order}
                    onChange={(event) => setDraft({ ...draft, order: event.target.value })}
                  />
                </Field>
              </div>

              <label className="inline-flex items-center gap-2 mb-4 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={!!draft.is_active}
                  onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })}
                />
                Sayfa yayında
              </label>

              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                <Field label="Sayfa İçeriği">
                  <textarea
                    ref={contentRef}
                    className="admin-input leading-7"
                    style={{ minHeight: 360, padding: 14 }}
                    value={draft.content}
                    onChange={(event) => setDraft({ ...draft, content: event.target.value })}
                    placeholder={'Ziyaretçinin göreceği içeriği yazın...\n\n## Başlık\n- Madde\n1. Numaralı madde\n**kalın metin**'}
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {TEXT_PLACEHOLDERS.map((placeholder) => (
                      <button
                        key={placeholder.key}
                        type="button"
                        onClick={() => insertPlaceholder(placeholder.key)}
                        title={`${placeholder.label} — imlecin olduğu yere ekler`}
                        className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-cyan-300 hover:bg-white/[0.08]"
                      >
                        {`{{${placeholder.key}}}`}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Biçimlendirme: <code className="text-slate-400">## Başlık</code>,{' '}
                    <code className="text-slate-400">### Alt başlık</code>,{' '}
                    <code className="text-slate-400">- madde</code>,{' '}
                    <code className="text-slate-400">1. madde</code>,{' '}
                    <code className="text-slate-400">**kalın**</code>. Adresler ve
                    e-postalar kendiliğinden bağlantıya dönüşür.
                  </p>
                </Field>
                <Field label="Canlı Önizleme">
                  <article className="min-h-[360px] rounded-xl border border-white/[0.07] bg-[#070b0f] p-6 text-sm leading-7 text-slate-400">
                    <h3 className="text-xl font-black mb-5 text-slate-100">
                      {renderSettingText(draft.title, settings)}
                    </h3>
                    {draft.content
                      ? <PageContent content={renderSettingText(draft.content, settings)} compact />
                      : 'İçerik önizlemesi burada görünür.'}
                  </article>
                </Field>
              </div>
            </>
          ) : (
            <div className="min-h-[500px] grid place-items-center text-center text-slate-500">
              <div>
                <FileText size={32} className="mx-auto mb-3 opacity-50" />
                <p>Düzenlemek için bir sayfa seçin veya yeni sayfa oluşturun.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);

export default FooterPages;
